import { ElementRef } from '@angular/core';
import * as d3 from 'd3';
import { timeFormatLocale, TimeLocaleDefinition } from 'd3-time-format';

import { ValueRange, ValueRangeType } from './value-range';
import { Visualization } from './visualization.interface';
import { I18nService, SettingsUtil, UserSettings } from '@lp-ui-lib';
import { generateSecureIdentifier } from './visualization-type';

// If BarRawPoint is exported elsewhere, import it from there. Otherwise, keep the local RawPoint below.
// import { BarRawPoint } from './bar.types';

const ANIMATION_DURATION = 0;

/** ---- Locale definitions (periods must be 2-tuple) ---- */
const LOCALE_EN: TimeLocaleDefinition = {
  dateTime: '%x, %X',
  date: '%-m/%-d/%Y',
  time: '%-I:%M:%S %p',
  periods: ['AM', 'PM'] as [string, string],
  days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  shortDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
};

const LOCALE_DE: TimeLocaleDefinition = {
  dateTime: '%A, der %e. %B %Y, %X',
  date: '%d.%m.%Y',
  time: '%H:%M:%S',
  periods: ['AM', 'PM'] as [string, string],
  days: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
  shortDays: ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'],
  months: [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ],
  shortMonths: ['Jan', 'Feb', 'Mrz', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
};

export enum BarChartType {
  Hour = 0,
  Day,
  Week,
  Month,
  Year,
}

interface BarRawPoint {
  measuredValue: string;
  measurementTypeID: number;
  timestamp: Date;
  value: number;
}

interface BarPoint {
  measuredValue: string;
  measurementTypeID: number;
  timestamp: Date;
  date: Date;
  value: number;
}

export class BarChart implements Visualization<BarRawPoint, UserSettings> {
  public type: BarChartType = BarChartType.Month;
  public original_type: BarChartType = BarChartType.Month;

  private x_domain: [Date, Date] | null = null; // visible x window
  private y_domain: [number, number] | null = null; // y range
  private valueRanges: ValueRange[] = [];
  private y_unit = '';
  private customRange = false;

  private data: BarPoint[] = [];
  private original_data: BarRawPoint[] = [];

  public updateChart: (() => void) | undefined;
  private resetBrush: (() => void) | undefined;
  private brushCallback: ((x_domain: [Date, Date]) => void) | undefined;
  private brushResetCallback: ((x_domain: [Date, Date]) => void) | undefined;

  private chartContainer: ElementRef<HTMLElement>;
  private chartContainerFixed: ElementRef<HTMLElement>;
  private scrollable: HTMLElement;

  constructor(
    private chartContainers: ElementRef<HTMLElement>[],
    private id: string,
    private i18n: I18nService
  ) {
    this.chartContainer = chartContainers[0];
    this.chartContainerFixed = chartContainers[1];
    this.scrollable = chartContainers[2].nativeElement ?? (chartContainers[2] as any);
  }

  // ----------------------------------------------------------
  // Public API (Visualization)
  // ----------------------------------------------------------
  public render(data: readonly BarRawPoint[], isMobile: boolean, settings: UserSettings): void {
    const incoming = data as BarRawPoint[];

    if (incoming.length) {
      this.original_data = incoming;
      this.data = this.groupData(incoming, this.type);

      // compute default y-domain (rounded to 500s, min separation 500)
      let minValue = Infinity;
      let maxValue = -Infinity;
      for (const d of this.data) {
        if (d.value < minValue) minValue = d.value;
        if (d.value > maxValue) maxValue = d.value;
      }
      minValue = Math.floor(minValue / 500) * 500;
      maxValue = Math.max(Math.ceil(maxValue / 500) * 500, 1000, minValue + 500);
      this.y_domain = [minValue, maxValue];
    }

    this.renderGraph(isMobile, settings);
  }

  public SetValueUnit(unit: string): void {
    this.y_unit = unit ?? '';
  }

  public SetXDomain(start: Date, end: Date, custom = false, unbrush = false): void {
    // align to day boundaries unless custom
    if (!custom && (end.getHours() > 0 || end.getMinutes() > 0)) {
      end = new Date(new Date(end).setDate(end.getDate()));
    }
    if (!custom) start = new Date(start.toDateString());
    if (!custom) end = new Date(end.toDateString());

    // choose granularity from span
    if (daysBetween(start, end) < 15) {
      this.type = BarChartType.Day;
    } else if (daysBetween(start, end) < 42) {
      this.type = BarChartType.Week;
    } else {
      this.type = BarChartType.Month;
    }

    if (!unbrush) {
      start = pruneTimestamp(start, this.type);
      end = pruneTimestamp(end, this.type, false, true);
    }

    this.x_domain = d3.extent([start, end], (d) => d) as [Date, Date];
    this.customRange = custom;
    this.updateChart?.();
  }

  public ResetXDomain(): void {
    this.x_domain = null;
  }

  public SetYDomain(
    _domain: [number, number],
    _booleanDomain = false,
    _measurementID?: number
  ): void {
    // keep noop; if you want to enforce a custom y-domain, implement here
  }

  public ResetYDomain(): void {
    this.y_domain = null;
  }

  public SetValueRanges(ranges: readonly ValueRange[]): void {
    this.valueRanges = (ranges ?? []) as ValueRange[];
  }

  public OnBrush(callback: (x_domain: [Date, Date]) => void): void {
    this.brushCallback = callback;
  }

  public OnBrushReset(callback: (x_domain: [Date, Date]) => void): void {
    this.brushResetCallback = callback;
  }

  public ResetBrush(): void {
    // your chart sets resetBrush inside renderGraph()
  }

  // ----------------------------------------------------------
  // Internal helpers
  // ----------------------------------------------------------
  private groupData(data: BarRawPoint[], type: BarChartType): BarPoint[] {
    function groupBy<T>(xs: T[], transform: (x: T) => string): Record<string, T[]> {
      return xs.reduce(
        (rv, x) => {
          const key = transform(x);
          (rv[key] = rv[key] || []).push(x);
          return rv;
        },
        {} as Record<string, T[]>
      );
    }

    const grouped = this.fillGaps(
      groupBy(data, (x) => pruneTimestamp(x.timestamp, type, true).toString()),
      type
    );

    return Object.keys(grouped)
      .map(
        (key) =>
          grouped[key].reduce(
            (a: any, b: any) =>
              new Object({
                measuredValue: '',
                measurementTypeID: b.measurementTypeID,
                timestamp: a.timestamp ? a.timestamp : pruneTimestamp(b.timestamp, type, true),
                date: a.timestamp ? a.timestamp : pruneTimestamp(b.timestamp, type, true),
                value: Math.round((a.value + b.value + Number.EPSILON) * 100) / 100,
              }),
            {
              measuredValue: '',
              measurementTypeID: null,
              timestamp: null,
              date: null,
              value: 0,
            }
          ) as any
      )
      .sort(
        (a: any, b: any) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      ) as BarPoint[];
  }

  private fillGaps(data: Record<string, any[]>, type: BarChartType) {
    // Fill missing intervals with zeros to avoid gaps
    const dates = Object.keys(data)
      .map((k) => new Date(k))
      .sort((a, b) => +a - +b);
    if (dates.length === 0) return data;

    const min = dates[0];
    const max = dates[dates.length - 1];

    let full_range: Date[] = [];
    switch (type) {
      case BarChartType.Hour:
        full_range = d3.timeHours(min, max);
        break;
      case BarChartType.Day:
        full_range = d3.timeDays(min, max);
        break;
      case BarChartType.Week:
        full_range = d3.timeDays(min, max).filter((date) => date.getDay() === 1);
        break;
      case BarChartType.Month:
        full_range = d3.timeMonths(min, max);
        break;
      case BarChartType.Year:
        full_range = d3.timeYears(min, max);
        break;
    }

    full_range.forEach((date) => {
      const d = pruneTimestamp(date, type, true);
      const k = d.toString();
      if (!data[k]) {
        const anyKey = min.toString();
        const exampleType = data[anyKey]?.[0]?.measurementTypeID ?? null;
        data[k] = [
          {
            measurementTypeID: exampleType,
            value: 0,
            measuredValue: '',
            timestamp: d.toISOString(),
          },
        ];
      }
    });
    return data;
  }

  private renderGraph(isMobile: boolean, settings: UserSettings) {
    // Clean up previous drawings
    d3.select(`svg#${this.id}`).remove();
    d3.select(`svg#${this.id}-fixed`).remove();

    const max_width = this.chartContainer.nativeElement.clientWidth;
    const max_height = this.chartContainer.nativeElement.clientHeight;
    const isSafari = navigator.userAgent.indexOf('Safari') !== -1;

    const SMALLER_TICK_THRESHOLD = 8;
    const MAX_TICKS_WIDE_SCREEN = 10;
    const MAX_TICKS_NARROW_SCREEN = 8;

    // tick factor ~ number_of_ticks / max_ticks
    let tick_factor: number;
    if (max_width > 375) {
      tick_factor =
        this.data.length > MAX_TICKS_WIDE_SCREEN ? this.data.length / MAX_TICKS_WIDE_SCREEN : 1.0;
    } else {
      tick_factor =
        this.data.length > MAX_TICKS_NARROW_SCREEN
          ? this.data.length / MAX_TICKS_NARROW_SCREEN
          : 1.0;
    }

    const margin = {
      top: 25 + 30, // +30 for focus value
      right: 65,
      bottom: 71,
      left: 0,
    };
    const width = max_width - margin.left - margin.right;
    const height = max_height - margin.top - margin.bottom;
    let total_width = width * tick_factor;

    if (height < 0) return;

    // locals across functions in this scope
    let scrollArea: d3.Selection<SVGGElement, unknown, null, undefined>;
    let _fixedArea: d3.Selection<SVGSVGElement, unknown, null, undefined>;
    let xAxis: d3.Selection<SVGGElement, unknown, null, undefined>;
    let yAxis: d3.Selection<SVGGElement, unknown, null, undefined>;
    let yearAxis: d3.Selection<SVGGElement, unknown, null, undefined>;
    let clipMask: d3.Selection<SVGGElement, unknown, null, undefined>;
    let focusLine: d3.Selection<SVGLineElement, unknown, null, undefined>;
    let focusValue: d3.Selection<SVGTextElement, unknown, null, undefined>;
    let svgBarsRects: d3.Selection<SVGRectElement, BarPoint, SVGGElement, unknown>;

    let unique_identifier: string;
    let element: HTMLElement;
    let elementFixed: HTMLElement;

    let x: d3.ScaleTime<number, number>;
    let y: d3.ScaleLinear<number, number>;
    let x_extent: [Date, Date];
    let y_extent: [number, number];

    let bisect: (data: BarPoint[], xMs: number) => number;
    let currentData: BarPoint;
    let sx: number;
    let _sy: number;
    let _warning: boolean;
    let critical: boolean;
    let noData: boolean;

    const drawChart = () => {
      init();
      drawAxes();
      drawClipmask();
      drawHorizontalLines();
      if (!noData) drawRanges();
      if (!noData) drawBars();
      if (!noData) drawFocus();
      initBrushing();
      if (noData) drawNoDataIndicator();
    };

    const init = () => {
      unique_identifier = generateSecureIdentifier();

      element = this.chartContainer.nativeElement;
      elementFixed = this.chartContainerFixed.nativeElement;

      noData = !this.data || this.data.length < 1;

      if (noData) {
        // seed with a tiny stub so we can compute scales
        this.data = [
          {
            date: new Date(new Date().setDate(new Date().getDate() - 7)),
            timestamp: new Date(new Date().setDate(new Date().getDate() - 7)),
            measuredValue: '',
            measurementTypeID: 0,
            value: 0,
          },
          {
            date: new Date(),
            timestamp: new Date(),
            measuredValue: '',
            measurementTypeID: 0,
            value: 100,
          },
        ];
      }

      x_extent = d3.extent(this.data, (d) => d.date) as [Date, Date];
      if (!this.x_domain) this.x_domain = [x_extent[0], x_extent[1]];

      y_extent = this.y_domain
        ? this.y_domain
        : [d3.min(this.data, (d) => +d.value) ?? 0, d3.max(this.data, (d) => +d.value) ?? 1];

      x = d3
        .scaleTime()
        .domain(x_extent)
        .range([20, total_width - 20]);
      y = d3.scaleLinear().domain(y_extent).range([height, 0]);

      bisect = (data: BarPoint[], xMs: number) => {
        const right = d3.bisector<BarPoint, number>((d) => +d.date).right(data, xMs);
        if (right === 0) return right;
        const left = right - 1;
        if (!data[right]) return left;
        const diff1 = xMs - +data[left].date;
        const diff2 = +data[right].date - xMs;
        return diff1 < diff2 ? left : right;
      };

      currentData = this.data[bisect(this.data, +x_extent[1])];
      sx = x(currentData.date);
      _sy = y(currentData.value);

      _warning = !!this.valueRanges.find(
        (range) =>
          range.type === ValueRangeType.yellow &&
          range.min <= currentData.value &&
          range.max >= currentData.value
      );
      critical = !!this.valueRanges.find(
        (range) =>
          range.type === ValueRangeType.red &&
          range.min <= currentData.value &&
          range.max >= currentData.value
      );

      initSVG();
    };

    const initSVG = () => {
      element.innerHTML = '';

      _fixedArea = d3
        .select(elementFixed)
        .append('svg')
        .attr('id', `${this.id}-fixed`)
        .attr('width', max_width)
        .attr('height', max_height)
        .style('position', 'absolute')
        .style('pointer-events', 'none')
        .style('z-index', 2);

      const containerSvg = d3
        .select(element)
        .append('svg')
        .attr('id', this.id)
        .attr('width', total_width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom);

      scrollArea = containerSvg
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // Horizontal scroll to the rightmost by default
      this.scrollable.scrollTo({ left: 999999 });

      // defs for optional filters (kept from your original)
      const defs = scrollArea.append('defs');
      const filter = defs
        .append('filter')
        .attr('id', 'pill-gradient')
        .attr('height', '1000%')
        .attr('width', '1000%')
        .attr('x', '-500%')
        .attr('y', '-500%');

      filter
        .append('feColorMatrix')
        .attr('type', 'matrix')
        .attr('values', '0 0 0 1 0  0 0 0 1 0  0 0 0 1 0  0 0 0 1 0');

      filter.append('feGaussianBlur').attr('stdDeviation', 2).attr('result', 'coloredBlur');
      const feMerge = filter.append('feMerge');
      feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    };

    const drawClipmask = () => {
      // everything outside this area is clipped
      scrollArea
        .append('defs')
        .append('clipPath')
        .attr('id', `bar-clip-${this.id}`)
        .append('rect')
        .attr('width', total_width)
        .attr('height', height + 10)
        .attr('x', 0)
        .attr('y', -5);

      clipMask = scrollArea.append('g').attr('clip-path', `url(#bar-clip-${this.id})`);
    };

    const drawAxes = () => {
      scrollArea
        .append('rect')
        .style('fill', '#ffffff')
        .style('cursor', 'pointer')
        .attr('x', -1)
        .attr('y', 0)
        .attr('width', width + 1)
        .attr('height', height);

      if (!noData) {
        xAxis = scrollArea
          .append('g')
          .attr('id', 'x-axis')
          .call(
            d3
              .axisBottom(x)
              .tickSize(height + 12)
              .tickFormat(() => '')
          )
          .call((g) => g.selectAll('.tick line').remove())
          .call((g) => g.select('.domain').remove())
          .call((g) =>
            g
              .selectAll('.tick text')
              .attr('fill', '#afb4b9') // <-- fill (not color)
              .attr('font-size', 12)
              .attr('font-family', 'Roboto')
              .attr('font-weight', '700')
              .attr('line-height', 18 as any)
              .attr('letter-spacing', 0 as any)
              .attr('transform', 'translate(0,-3)')
          );

        yearAxis = scrollArea
          .append('g')
          .attr('id', 'year-axis')
          .call(
            d3
              .axisBottom(x)
              .tickSize(height + 28)
              .ticks(d3.timeYear)
              .tickPadding(15)
              .tickFormat((d: any) => d3.timeFormat('%Y')(d))
          )
          .call((g) => g.selectAll('.tick line').remove())
          .call((g) => g.select('.domain').remove())
          .call((g) => g.selectAll('.tick text').attr('fill', '#afb4b9')); // <-- fill
      }

      // yAxis on fixed svg (right-side)
      yAxis = d3
        .select(elementFixed)
        .select('svg')
        .append('g')
        .attr('id', 'y-axis')
        .attr('transform', `translate(${width}, ${margin.top})`)
        .call(
          d3
            .axisRight(y)
            .tickValues([y_extent[0], y_extent[1]])
            .tickSize(-total_width)
            .tickPadding(12)
            .tickFormat((d: any) => d + ' ' + this.y_unit)
        )
        .call((g) => g.select('.domain').remove())
        .call((g) => g.selectAll('.tick line').attr('stroke', '#e6e7e7'))
        .call((g) =>
          g
            .selectAll('.tick text')
            .attr('x', 4)
            .attr('fill', '#afb4b9') // <-- fill
            .attr('font-size', 12)
            .attr('font-family', 'Roboto')
            .attr('font-weight', '700')
            .attr('line-height', 18 as any)
            .attr('letter-spacing', 0 as any)
            .attr('text-anchor', 'end')
            .attr('dx', 35)
        );
    };

    let unbrushed_domain: [Date, Date] | null = null;

    const initBrushing = () => {
      // Remove old brush if update
      clipMask.selectAll('.brush').remove();

      const brush = d3
        .brushX()
        .extent([
          [0, -6],
          [total_width, height + 12],
        ])
        .on('end', (event: d3.D3BrushEvent<any>) => {
          if (!event.sourceEvent) return;
          const extent = event.selection as [number, number] | null;
          const epsilon = 0.008 * Math.abs(+this.x_domain![1] - +this.x_domain![0]);

          if (
            !extent ||
            !extent[0] ||
            Math.abs(+x.invert(extent[1]) - +x.invert(extent[0])) < epsilon
          ) {
            const px = extent ? extent[0] : (event.sourceEvent as MouseEvent).offsetX;
            mousemove(event, px);
          } else {
            if (minutesBetween(x.invert(extent[0]), x.invert(extent[1])) > 60) {
              if (!unbrushed_domain) unbrushed_domain = this.x_domain!;
              this.SetXDomain(x.invert(extent[0]), x.invert(extent[1]), true);
              this.brushCallback?.(this.x_domain!);
            }
          }
          // remove grey selection
          setTimeout(() => clipMask.select('.brush').call(brush.move as any, null));
        });

      this.resetBrush = () => {
        if (unbrushed_domain) this.SetXDomain(unbrushed_domain[0], unbrushed_domain[1]);
        unbrushed_domain = null;
        this.brushResetCallback?.(this.x_domain!);
        updateFocusline(this.data[bisect(this.data, +this.x_domain![1])].date, true);
      };

      // double-click reset brush
      scrollArea.on('dblclick', () => this.resetBrush?.());

      // attach brush
      clipMask
        .append('g')
        .attr('class', 'brush')
        .call(brush as any);
      clipMask.selectAll('.selection').attr('fill-opacity', 1);
    };

    const drawHorizontalLines = () => {
      d3.select(elementFixed)
        .select('svg')
        .append('line')
        .attr('x1', 0)
        .attr('x2', max_width)
        .attr('y1', height + 31 + margin.top)
        .attr('y2', height + 31 + margin.top)
        .attr('stroke', '#e6e7e7');
    };

    const drawNoDataIndicator = () => {
      d3.select(elementFixed)
        .select('svg')
        .append('text')
        .text('No data')
        .attr('y', 16 + +margin.top)
        .attr('x', 36)
        .style('opacity', 0.6);
    };

    const drawRanges = () => {
      for (const range of this.valueRanges) {
        if (range.type === ValueRangeType.red) {
          // fill red region only if the current value is in critical range
          if (critical) {
            clipMask
              .append('rect')
              .style('fill', '#E1000F')
              .style('cursor', 'pointer')
              .style('opacity', 0.05)
              .attr('x', 0)
              .attr('y', y(range.max))
              .attr('width', width - margin.right)
              .attr('height', y(range.min) - y(range.max));
          }
          if (range.min > y_extent[0]) {
            clipMask
              .append('line')
              .attr('x1', 0)
              .attr('x2', width)
              .attr('y1', y(range.min))
              .attr('y2', y(range.min))
              .attr('stroke', '#E1000F')
              .style('cursor', 'pointer');
          }
          if (range.max < y_extent[1]) {
            clipMask
              .append('line')
              .attr('x1', 0)
              .attr('x2', width)
              .attr('y1', y(range.max))
              .attr('y2', y(range.max))
              .attr('stroke', '#E1000F')
              .style('cursor', 'pointer');
          }
        }
      }

      // Critical gradient (from your original helper)
      clipMask
        .append('linearGradient')
        .attr('id', 'line-gradient' + unique_identifier)
        .attr('gradientUnits', 'userSpaceOnUse')
        .attr('x1', 0)
        .attr('y1', y(y_extent[0]))
        .attr('x2', 0)
        .attr('y2', y(y_extent[1]))
        .selectAll('stop')
        .data(generateCriticalGradient(this.valueRanges, y_extent, critical) as any)
        .enter()
        .append('stop')
        .attr('offset', (d: any) => d.offset)
        .attr('stop-color', (d: any) => d.color);
    };

    const drawBars = () => {
      if (svgBarsRects) {
        clipMask.selectAll('.bar').attr('class', 'old bar');
      }

      const svgBars = clipMask.append('g').selectAll('this_is_empty').data(this.data).enter();

      svgBarsRects = svgBars
        .append('rect')
        .attr('class', 'bar')
        .attr('rx', 3)
        .attr('ry', 3)
        .attr('x', (d) => x(d.date) - 10)
        .attr('y', (d) => Math.min(height - 1, y(d.value)))
        .attr('width', 21)
        .attr('height', (d) => height - Math.min(height - 1, y(d.value)))
        .attr('stroke', 'none')
        .attr('fill', '#C4C4C4');
    };

    const drawFocus = () => {
      if (noData) return;

      focusLine = scrollArea
        .append('line')
        .attr('x1', sx)
        .attr(
          'y1',
          y(this.y_domain ? this.y_domain[1] : (d3.max(this.data, (d) => +d.value) ?? 0)) - 8
        )
        .attr('x2', sx)
        .attr('y2', y(this.y_domain ? this.y_domain[0] : (d3.min(this.data, (d) => +d.value) ?? 0)))
        .attr('stroke', 'black')
        .style('cursor', 'pointer');

      focusValue = scrollArea
        .append('text')
        .text((this.data[this.data.length - 1].value ?? '') + this.y_unit)
        .attr('x', sx)
        .attr('y', 0)
        .attr('fill', 'black')
        .attr('font-size', 14)
        .attr('font-family', 'Roboto')
        .attr('font-weight', '700')
        .attr('line-height', 18 as any)
        .attr('letter-spacing', 0 as any)
        .attr('text-anchor', 'middle')
        .attr('dy', -12);
    };

    this.updateChart = () => {
      if (this.original_type !== this.type) {
        if (!noData) {
          this.data = this.groupData(this.original_data, this.type);

          // recompute y extents
          let minValue = Infinity;
          let maxValue = -Infinity;
          for (const d of this.data) {
            if (d.value < minValue) minValue = d.value;
            if (d.value > maxValue) maxValue = d.value;
          }
          minValue = Math.floor(minValue / 500) * 500;
          maxValue = Math.max(Math.ceil(maxValue / 500) * 500, 1000, minValue + 500);
          this.y_domain = [minValue, maxValue];

          y_extent = this.y_domain;
          y = d3.scaleLinear().domain(y_extent).range([height, 0]);

          drawBars();
        }
        this.original_type = this.type;
      }

      const tick_num = this.data.filter(
        (d) => d.timestamp >= this.x_domain![0] && d.timestamp <= this.x_domain![1]
      ).length;

      if (max_width > 375) {
        tick_factor = tick_num > MAX_TICKS_WIDE_SCREEN ? tick_num / MAX_TICKS_WIDE_SCREEN : 1.0;
      } else {
        tick_factor = tick_num > MAX_TICKS_NARROW_SCREEN ? tick_num / MAX_TICKS_NARROW_SCREEN : 1.0;
      }

      if (total_width !== tick_factor * width) {
        updateScrollbar(tick_factor);
      }

      this.scrollable.scrollTo({ left: 999999 });

      if (!noData) updateXAxis();
      if (!noData) updateYAxis();
      if (!noData) updateBarsPositions();

      initBrushing();
      mouseout();
    };

    const updateBarsPositions = () => {
      clipMask
        .selectAll<SVGRectElement, BarPoint>('.bar')
        .transition()
        .duration(ANIMATION_DURATION)
        .attr('x', (d) => x(d.date) - 10);
    };
    let selectedData: BarPoint | undefined;

    const mousemove = (event: any, forcedX?: number) => {
      if (noData) return;
      const [px] = forcedX != null ? [forcedX] : d3.pointer(event, event.currentTarget);
      updateFocusline(x.invert(px));
    };

    const mouseout = () => {
      if (noData) return;
      updateFocusline(this.data[bisect(this.data, +this.x_domain![1])].date, true);
    };

    const updateFocusline = (x0: Date, reset = false) => {
      if (noData) return;

      const temp_data = this.data.filter(
        (item) => item.date > this.x_domain![0] && item.date < this.x_domain![1]
      );
      if (!temp_data.length) return;

      const i = bisect(temp_data, +x0);
      selectedData = temp_data[i];

      let _sx = x(selectedData.date);
      _sx = Math.max(0, Math.min(_sx, total_width));

      focusLine
        .transition()
        .duration(reset ? ANIMATION_DURATION : 500)
        .attr('x1', _sx)
        .attr('x2', _sx);

      _sy = y(selectedData.value);

      clipMask
        .selectAll<SVGRectElement, BarPoint>('.bar')
        .transition()
        .duration(reset ? ANIMATION_DURATION : 500)
        .attr('x', (d) => x(d.date) - 10)
        .attr('fill', (d) =>
          +selectedData!.date === +d.date
            ? '#5F6973'
            : d.date < this.x_domain![0]
              ? '#C4C4C400'
              : '#C4C4C4'
        );

      clipMask
        .selectAll('.old')
        .transition()
        .duration(reset ? ANIMATION_DURATION : 500)
        .attr('x', (d: any) => x(d.date) - 10)
        .attr('fill', (d: any) => (+selectedData!.date === +d.date ? '#5F697300' : '#C4C4C400'));

      setTimeout(
        () => {
          clipMask.selectAll('.old').remove();
        },
        reset ? ANIMATION_DURATION : 500
      );

      xAxis
        .selectAll<SVGTextElement, Date>('.tick text')
        .transition()
        .duration(ANIMATION_DURATION)
        .attr('fill', (d) => (+selectedData!.date === +d ? '#5F6973' : '#C4C4C4'));

      focusValue
        .text(`${selectedData.value} ${this.y_unit}`)
        .transition()
        .duration(reset ? ANIMATION_DURATION : 500)
        .attr('x', _sx);
    };

    const updateScrollbar = (tf: number) => {
      total_width = tf * width;
      x.range([20, total_width - 20]);

      d3.select(element)
        .select<SVGSVGElement>('svg')
        .attr('width', total_width + margin.left + margin.right)
        .style('width', total_width + margin.left + margin.right + 'px');

      scrollArea.select(`#bar-clip-${this.id}`).select('rect').attr('width', total_width);
    };

    const updateYAxis = () => {
      yAxis.call(
        d3
          .axisRight(y)
          .tickValues([y_extent[0], y_extent[1]])
          .tickSize(-total_width)
          .tickPadding(12)
          .tickFormat((d: any) => d + ' ' + this.y_unit)
      );
    };

    const updateXAxis = () => {
      if (this.x_domain) {
        x.domain(this.x_domain);
      }
      x.range([20, total_width - 20]);

      if (!this.customRange) unbrushed_domain = null;

      const tickCall = generateXTicks(this.x_domain![0], this.x_domain![1]);
      xAxis
        .transition()
        .duration(ANIMATION_DURATION)
        .call(tickCall)
        .call((g) => g.select('.domain').remove())
        .call((g) =>
          g
            .selectAll('.tick text')
            .attr('fill', '#afb4b9') // <-- fill
            .attr('font-size', isMobile ? '12px' : '14px')
            .attr('font-family', 'Roboto')
            .attr('font-weight', '700')
            .attr('line-height', 18 as any)
            .attr('letter-spacing', 0 as any)
            .attr('transform', 'translate(0,-3)')
        );

      xAxis.call((g) => g.selectAll('.tick line').remove());

      const yearTransitionInRange =
        this.x_domain![0].getFullYear() !== this.x_domain![1].getFullYear();

      yearAxis
        .transition()
        .duration(ANIMATION_DURATION)
        .call(
          d3
            .axisBottom(x)
            .tickSize(height + (isSafari ? 30 : 28))
            .tickValues(
              yearTransitionInRange
                ? d3.timeYear.range(this.x_domain![0], this.x_domain![1])
                : [new Date((+this.x_domain![0] + +this.x_domain![1]) / 2)]
            )
            .tickPadding(15)
            .tickFormat((d: any) => (noData ? '' : d3.timeFormat('%Y')(d)))
        )
        .call((g) => g.selectAll('.tick line').attr('stroke', 'transparent'))
        .call((g) => g.select('.domain').remove())
        .call((g) =>
          g
            .selectAll('.tick text')
            .attr('fill', '#afb4b9') // <-- fill
            .attr('font-size', 12)
            .attr('font-family', 'Roboto')
            .attr('font-weight', '700')
            .attr('line-height', 18 as any)
            .attr('letter-spacing', 0 as any)
            .attr('dx', yearTransitionInRange ? 0 : (margin.right - margin.left) / 2)
        )
        .call((g) => g.selectAll('.tick:last-child text').attr('fill', 'black')); // <-- fill
    };

    const generateXTicks = (start: Date, end: Date) => {
      const tick_num = this.data.filter(
        (d) => d.timestamp >= this.x_domain![0] && d.timestamp <= this.x_domain![1]
      ).length;

      const small_screen1 = width < 450 || tick_num > SMALLER_TICK_THRESHOLD;
      const small_screen2 = width < 350 || tick_num > SMALLER_TICK_THRESHOLD;

      const abbr =
        SettingsUtil.getLanguage(settings.languages, settings.languageID)?.abbreviation ?? 'en';
      const locale = timeFormatLocale(abbr === 'de' ? LOCALE_DE : LOCALE_EN);

      const tickValuesRangeEnd = minDate(new Date(this.data[this.data.length - 1].date), end);
      const tickValuesRangeStart = start;

      let tickFormat = (d: Date) =>
        locale.format(getTimeFormat(this.type, small_screen1, small_screen2, settings))(d);

      const tickValues = this.data
        .map((item) => item.date)
        .filter(
          (item) =>
            item.getTime() > tickValuesRangeStart.getTime() &&
            item.getTime() <= tickValuesRangeEnd.getTime()
        );

      if (noData) {
        tickFormat = () => '';
      }

      return d3
        .axisBottom(x)
        .tickSize(height)
        .tickValues(tickValues)
        .tickPadding(12)
        .tickFormat(tickFormat as any);
    };

    // initial draw
    drawChart();
    setTimeout(() => this.updateChart?.(), 0);
  }
}

// ----------------------------------------------------------
// Utility functions (unchanged logic; typed)
// ----------------------------------------------------------
function treatAsUTC(date: Date | string): number {
  const result = new Date(date);
  result.setMinutes(result.getMinutes() - result.getTimezoneOffset());
  return +result;
}
function daysBetween(startDate: Date, endDate: Date): number {
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  return (treatAsUTC(endDate) - treatAsUTC(startDate)) / millisecondsPerDay;
}
function minutesBetween(startDate: Date, endDate: Date): number {
  const millisecondsPerMinute = 60 * 1000;
  return (treatAsUTC(endDate) - treatAsUTC(startDate)) / millisecondsPerMinute;
}
function minDate(a: Date, b: Date) {
  return a < b ? a : b;
}

function getTimeFormat(
  type: BarChartType,
  small_screen1 = false,
  small_screen2 = false,
  settings: UserSettings
) {
  switch (type) {
    case BarChartType.Hour:
      return '%H:%M';
    case BarChartType.Day:
      return small_screen2
        ? '%d'
        : !!SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID) &&
            SettingsUtil.getDateFormat(settings.dateFormats, settings.dateFormatID)
              ?.formatString === 'MM/dd/yyyy'
          ? '%m/%d'
          : '%d-%m';
    case BarChartType.Week:
      return (
        (SettingsUtil.getLanguage(settings.languages, settings.languageID)?.abbreviation === 'de'
          ? 'KW'
          : 'CW') + '%U'
      );
    case BarChartType.Month:
      return small_screen1 ? '%b' : '%B';
    case BarChartType.Year:
      return '%Y';
  }
}

function generateCriticalGradient(
  ranges: ValueRange[],
  y_extent: [number, number],
  critical: boolean
) {
  if (!critical) {
    return [
      { offset: '-100%', color: 'black' },
      { offset: '100%', color: 'black' },
    ];
  }
  let above_crit_offsets: { offset: string; color: string }[] = [];
  let below_crit_offsets: { offset: string; color: string }[] = [];

  const above_crit_range = ranges.find(
    (red_range) =>
      red_range.type === ValueRangeType.red &&
      !!ranges.find(
        (green_range) =>
          green_range.type === ValueRangeType.green && green_range.max <= red_range.min
      )
  );

  const below_crit_range = ranges.find(
    (red_range) =>
      red_range.type === ValueRangeType.red &&
      !!ranges.find(
        (green_range) =>
          green_range.type === ValueRangeType.green && green_range.min >= red_range.max
      )
  );

  const full_range = y_extent[1] - y_extent[0];
  const offset = full_range * 0.05;

  if (above_crit_range) {
    const gradient_start = above_crit_range.min - offset - y_extent[0];
    const gradient_end = above_crit_range.min + offset - y_extent[0];

    above_crit_offsets = [
      { offset: ((gradient_start / full_range) * 100).toFixed(0) + '%', color: 'black' },
      { offset: ((gradient_end / full_range) * 100).toFixed(0) + '%', color: '#E1000F' },
    ];
  }

  if (below_crit_range) {
    const gradient_start = below_crit_range.max - offset - y_extent[0];
    const gradient_end = below_crit_range.max + offset - y_extent[0];

    below_crit_offsets = [
      { offset: ((gradient_start / full_range) * 100).toFixed(0) + '%', color: '#E1000F' },
      { offset: ((gradient_end / full_range) * 100).toFixed(0) + '%', color: 'black' },
    ];
  }

  return [
    { offset: '-100%', color: below_crit_range ? '#E1000F' : 'black' },
    ...below_crit_offsets,
    ...above_crit_offsets,
    { offset: '100%', color: above_crit_range ? '#E1000F' : 'black' },
  ];
}

function pruneTimestamp(
  timestamp: Date,
  _type: BarChartType,
  center = false,
  roundUp = false
): Date {
  const out = new Date(timestamp);
  switch (_type) {
    case BarChartType.Year:
      out.setMonth(center ? 6 : 0);
      if (roundUp) out.setFullYear(out.getFullYear() + 1);
      out.setDate(15);
      out.setHours(12);
      break;
    case BarChartType.Month:
      out.setDate(center ? 15 : 1);
      if (roundUp) out.setMonth(out.getMonth() + 1);
      out.setHours(12);
      break;
    case BarChartType.Week: {
      const day = out.getDay();
      const diff = out.getDate() - day + (center ? 3 : day === 0 ? -6 : 1);
      out.setDate(diff + (roundUp ? 7 : 0));
      out.setHours(12);
      break;
    }
    case BarChartType.Day:
      out.setHours(center ? 12 : 0);
      if (roundUp) out.setDate(out.getDate() + 1);
      break;
  }
  out.setSeconds(0, 0);
  out.setMinutes(0);
  return out;
}
